package fr.frux.feature.treeadd.wizard

import fr.frux.core.common.error.AppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.shared.content.SpeciesSuggestionLimits
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.DuplicateCandidate
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.findDuplicateCandidates
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private const val TAG = "SpeciesStepController"

/**
 * Logique de l'étape Espèce, extraite de [TreeWizardViewModel] (detekt `TooManyFunctions`) : le
 * ViewModel l'expose en `species`, les écrans l'appellent directement. [updateState] agit sur
 * l'état de l'assistant, [scope] est son `viewModelScope`.
 */
class SpeciesStepController internal constructor(
    private val speciesRepository: SpeciesRepository,
    private val treeRepository: TreeRepository,
    private val updateState: ((TreeWizardState) -> TreeWizardState) -> Unit,
    private val scope: CoroutineScope,
    private val currentMonth: () -> Int,
) {
    private var referential: List<Species> = emptyList()

    /** `null` tant que les comptes ne sont pas connus, ou si leur appel a échoué. */
    private var counts: Map<Long, Int>? = null
    private var nearby: List<Tree> = emptyList()
    private var position: Location? = null
    private var query: String = ""
    private var searchJob: Job? = null
    private var aroundJob: Job? = null
    private var referentialJob: Job? = null
    private var suggestionSending = false

    fun onQueryChange(query: String) {
        this.query = query
        updateState { it.withSpecies { s -> s.copy(query = query, selected = null, duplicate = null) } }
        searchJob?.cancel()
        if (query.trim().length < MIN_SEARCH_QUERY_LENGTH) {
            // Effacer la saisie rend la grille du coin tout de suite, sans debounce.
            rebuildGrid()
            return
        }
        searchJob =
            scope.launch {
                delay(SEARCH_DEBOUNCE_MS)
                rebuildGrid()
            }
    }

    fun clearQuery() = onQueryChange("")

    fun select(species: Species) {
        val duplicate = duplicateFor(species)
        updateState {
            it.withSpecies { s -> s.copy(selected = species, certainty = Certainty.PROBABLE, duplicate = duplicate) }
        }
    }

    private fun duplicateFor(species: Species?): DuplicateCandidate? {
        val here = position
        if (here == null || species == null) return null
        return findDuplicateCandidates(nearby, here, species.id) { id -> referential.find { it.id == id }?.rollupId }
            .firstOrNull()
    }

    /** Rouvre la grille en gardant la recherche : on repart de ce qu'on avait tapé. */
    fun change() {
        updateState { it.withSpecies { s -> s.copy(selected = null, duplicate = null) } }
    }

    fun selectCertainty(certainty: Certainty) {
        updateState { it.withSpecies { s -> s.copy(certainty = certainty) } }
    }

    fun retry() = loadReferential()

    fun showSuggestion() {
        updateState { it.withSuggestion { SuggestionUi(dialogVisible = true) } }
    }

    fun hideSuggestion() {
        updateState { it.withSuggestion { SuggestionUi() } }
    }

    /**
     * Journal libre : l'arbre ne se crée jamais sous l'espèce suggérée. Un échec n'est pas bloquant,
     * le dialogue reste ouvert et l'erreur se lit dans l'état. Le quota (429) se distingue d'une
     * panne réseau pour ne pas laisser croire que le serveur n'a pas répondu.
     */
    fun submitSuggestion(
        name: String,
        comment: String?,
    ) {
        if (suggestionSending) return
        suggestionSending = true
        updateState { it.withSuggestion { ui -> ui.copy(sending = true, error = null) } }
        scope.launch {
            speciesRepository
                .suggestSpecies(
                    name.trim().take(SpeciesSuggestionLimits.NAME_MAX),
                    comment?.trim()?.take(SpeciesSuggestionLimits.COMMENT_MAX)?.ifEmpty { null },
                ).fold(
                    onSuccess = {
                        updateState { it.withSuggestion { SuggestionUi(sent = true) } }
                    },
                    onFailure = { error ->
                        Logger.e(TAG, "Échec de l'envoi de la suggestion d'espèce", error)
                        val kind =
                            if (error is AppError.RateLimited && error.quotaExhausted) {
                                AddTreeSuggestionError.Quota
                            } else {
                                AddTreeSuggestionError.Network
                            }
                        updateState { it.withSuggestion { ui -> ui.copy(sending = false, error = kind) } }
                    },
                )
            suggestionSending = false
        }
    }

    fun consumeSuggestionSent() {
        updateState { it.withSuggestion { ui -> ui.copy(sent = false) } }
    }

    /** Référentiel complet en un appel (ETag côté dépôt), filtré ensuite localement. */
    internal fun loadReferential() {
        if (referentialJob?.isActive == true) return
        updateState { it.withSpecies { s -> s.copy(referential = ReferentialLoad.Loading) } }
        referentialJob =
            scope.launch {
                speciesRepository.getSpecies().fold(
                    onSuccess = { list ->
                        referential = list
                        updateState { it.withSpecies { s -> s.copy(referential = ReferentialLoad.Loaded) } }
                        rebuildGrid()
                    },
                    onFailure = { error ->
                        Logger.e(TAG, "Référentiel d'espèces indisponible", error)
                        updateState { it.withSpecies { s -> s.copy(referential = ReferentialLoad.Failed) } }
                    },
                )
            }
    }

    /**
     * Un appel unique autour de la position figée de l'étape 1 : comptes de la grille et arbres du
     * rappel de doublon. Un échec laisse les comptes inconnus, la grille se rabat sur la saison.
     */
    internal fun loadAround(position: Location) {
        aroundJob?.cancel()
        this.position = position
        nearby = emptyList()
        counts = null
        rebuildGrid(refreshDuplicate = true)
        aroundJob =
            scope.launch {
                treeRepository
                    .nearbyTreesSnapshot(
                        lat = position.latitude,
                        lon = position.longitude,
                        radiusMeters = NEARBY_SPECIES_RADIUS_METERS,
                        limit = NEARBY_SPECIES_LIMIT,
                    ).fold(
                        onSuccess = { trees ->
                            nearby = trees
                            counts = countBySpecies(trees)
                        },
                        onFailure = { error ->
                            Logger.e(TAG, "Comptes d'espèces indisponibles, repli sur la saison", error)
                            nearby = emptyList()
                            counts = null
                        },
                    )
                // Un choix fait avant la réponse (ou gardé d'une position précédente) est re-vérifié.
                rebuildGrid(refreshDuplicate = true)
            }
    }

    private fun rebuildGrid(refreshDuplicate: Boolean = false) {
        val trimmed = query.trim()
        val grid =
            if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) {
                aroundGrid(referential, counts, currentMonth())
            } else {
                searchGrid(referential, counts, trimmed)
            }
        updateState {
            it.withSpecies { s ->
                s.copy(grid = grid, duplicate = if (refreshDuplicate) duplicateFor(s.selected) else s.duplicate)
            }
        }
    }
}

private inline fun TreeWizardState.withSpecies(transform: (SpeciesStepState) -> SpeciesStepState): TreeWizardState =
    copy(species = transform(species))

private inline fun TreeWizardState.withSuggestion(transform: (SuggestionUi) -> SuggestionUi): TreeWizardState =
    withSpecies { it.copy(suggestion = transform(it.suggestion)) }
